export interface GtmConfig {
  gtmId: string
  auth?: string
  preview?: string
}

export type GtmConsentMode = 'default' | 'update'

export type GtmConsentStorage = 'granted' | 'denied'

export type CookieConsentForGtm = {
  analytics?: boolean
  marketing?: boolean
  performance?: boolean
}

export type GtmConsentPayload = {
  analytics_storage: GtmConsentStorage
  ad_storage: GtmConsentStorage
  ad_user_data: GtmConsentStorage
  ad_personalization: GtmConsentStorage
  functionality_storage: GtmConsentStorage
}

export const DEFAULT_DENIED_COOKIE_CONSENT: CookieConsentForGtm = {
  analytics: false,
  marketing: false,
  performance: false,
}

type GtmWindow = Window & {
  dataLayer?: unknown[]
  gtag?: GtagFunction
}

type GtagFunction = {
  (...args: unknown[]): void
  (command: 'consent', mode: GtmConsentMode, payload: GtmConsentPayload): void
}

function ensureGtagShim(gtmWindow: GtmWindow) {
  gtmWindow.dataLayer = gtmWindow.dataLayer || []
  if (gtmWindow.gtag) return

  // Google Consent Mode requires the standard gtag shim (arguments object, not a rest-param array).
  gtmWindow.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params -- matches Google's documented gtag implementation
    gtmWindow.dataLayer!.push(arguments)
  } as GtagFunction
}

export function getGtmConfig(): GtmConfig {
  return {
    gtmId: process.env.NEXT_PUBLIC_ANALYTICS_GTM_ID!,
    auth: process.env.NEXT_PUBLIC_ANALYTICS_GTM_AUTH!,
    preview: process.env.NEXT_PUBLIC_ANALYTICS_GTM_PREVIEW!,
  }
}

export function mapCookieConsentToGtm(consent: CookieConsentForGtm): GtmConsentPayload {
  const analyticsStorage: GtmConsentStorage = consent.analytics ? 'granted' : 'denied'
  const adStorage: GtmConsentStorage = consent.marketing ? 'granted' : 'denied'
  const functionalityStorage: GtmConsentStorage = consent.performance ? 'granted' : 'denied'

  return {
    analytics_storage: analyticsStorage,
    ad_storage: adStorage,
    ad_user_data: adStorage,
    ad_personalization: adStorage,
    functionality_storage: functionalityStorage,
  }
}

/** Sends Consent Mode v2 updates through gtag — not raw dataLayer array pushes. */
export function pushGtmConsent(mode: GtmConsentMode, consent: CookieConsentForGtm) {
  if (typeof window === 'undefined') return

  const gtmWindow = window as GtmWindow
  ensureGtagShim(gtmWindow)
  gtmWindow.gtag!('consent', mode, mapCookieConsentToGtm(consent))
}
